import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import Header from './components/layout/Header';
import StoreConnectionPage from './pages/StoreConnectionPage';
import ProductAnalyticsPage from './pages/ProductAnalyticsPage';
import KeywordMetricsPage from './pages/KeywordMetricsPage';
import RecommendationsPage from './pages/RecommendationsPage';
import ImportedProductsPage from './pages/ImportedProductsPage';
import StoreSettingsPage from './pages/StoreSettingsPage';
import ImageTranslatorPage from './pages/ImageTranslatorPage';
import AIImageGeneratorPage from './pages/AIImageGeneratorPage';
import TaobaoSearchPage from './pages/TaobaoSearchPage';
import BulkImporterPage from './pages/BulkImporterPage';
import AIImageQueuePage from './pages/AIImageQueuePage';
import Login from './pages/Login';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './index.css';
import { UserProvider, useUser } from './context/UserContext';
import AdminPasswordPrompt from './components/AdminPasswordPrompt';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#4338ca', // Indigo-600
      light: '#6366f1',
      dark: '#3730a3',
      contrastText: '#ffffff',
    },
    secondary: {
      main: '#ec4899', // Pink-500
      light: '#f472b6',
      dark: '#be185d',
      contrastText: '#ffffff',
    },
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#0f172a',
      secondary: '#475569',
    },
  },
  shape: {
    borderRadius: 12,
  },
  typography: {
    fontFamily: '"Inter", "Outfit", "Segoe UI", Roboto, "Helvetica Neue", sans-serif',
    h4: {
      fontWeight: 700,
      letterSpacing: '-0.02em',
      color: '#0f172a',
    },
    h5: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    subtitle1: {
      fontWeight: 500,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0px 4px 6px -1px rgba(0,0,0,0.1), 0px 2px 4px -1px rgba(0,0,0,0.06)',
            transform: 'translateY(-1px)',
          },
          transition: 'all 0.2s ease-in-out',
        },
        contained: {
          background: 'linear-gradient(135deg, #4338ca 0%, #6366f1 100%)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.05), 0 2px 4px -2px rgb(0 0 0 / 0.05)',
          border: '1px solid #e2e8f0',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(12px)',
          borderBottom: '1px solid #e2e8f0',
          boxShadow: 'none',
          color: '#0f172a',
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          height: 3,
          borderTopLeftRadius: 3,
          borderTopRightRadius: 3,
          backgroundColor: '#4338ca',
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          color: '#64748b',
          transition: 'all 0.2s',
          '&.Mui-selected': {
            color: '#4338ca',
          },
          '&:hover': {
            color: '#4338ca',
            backgroundColor: 'rgba(67, 56, 202, 0.04)',
          },
        },
      },
    },
  },
});

// Create a wrapper component for protected routes
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useUser();

  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return children;
};

function AppContent() {
  const { isAuthenticated, isLoading, requiresAdminPassword } = useUser();
  const [activeTab, setActiveTab] = useState('store-connection');

  // Show loading state
  if (isLoading) {
    return <div className="flex items-center justify-center min-h-screen">Loading...</div>;
  }

  // Show admin password prompt if required
  if (requiresAdminPassword) {
    return <AdminPasswordPrompt />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const handleTabClick = (tabId) => {
    setActiveTab(tabId);
  };

  const renderActivePage = () => {
    const pages = {
      'store-connection': <StoreConnectionPage />,
      'product-analytics': <ProductAnalyticsPage />,
      'keyword-metrics': <KeywordMetricsPage />,
      'recommendations': <RecommendationsPage />,
      'imported-products': <ImportedProductsPage />,
      'settings': <StoreSettingsPage />,
      'image-translator': <ImageTranslatorPage />,
      'ai-image-gen': <AIImageGeneratorPage />,
      'taobao-search': <TaobaoSearchPage />,
      'bulk-importer': <BulkImporterPage />,
      'image-queue':   <AIImageQueuePage />,
    };

    return pages[activeTab] || <StoreConnectionPage />;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header activeTab={activeTab} handleTabClick={handleTabClick} />
      <main className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {renderActivePage()}
        </div>
      </main>
    </div>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Router>
        <UserProvider>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route
              path="*"
              element={
                <ProtectedRoute>
                  <AppContent />
                </ProtectedRoute>
              }
            />
          </Routes>
        </UserProvider>
      </Router>
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
    </ThemeProvider>
  );
}

export default App;
